import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import { Menu as MenuIcon, AccountCircle, Dashboard, Logout, AdminPanelSettings } from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import FXWarsLogo from './FXWarsLogo';

// Admin email list - should match the one in AdminPortalPage
const ADMIN_EMAILS = [
  'admin@t1fxteam.co.za',
  'support@t1fxteam.co.za',
  'stephan@t1fxteam.co.za',
  'monique@t1fxteam.co.za',
  'walter@t1fxteam.co.za',
  'dewald@t1fxteam.co.za',
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userProfile, logout } = useAuth();

  // Check if current user is an admin
  const isAdmin = currentUser && ADMIN_EMAILS.includes(currentUser.email);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleMenuClose();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Base navigation items that everyone sees
  const baseNavItems = [
    { name: 'Home', path: '/' },
    { name: 'Prizes', path: '/prizes' },
    { name: 'Rules', path: '/rules' },
    { name: 'Leaderboard', path: '/leaderboard' },
    { name: 'Contact', path: '/contact' }
  ];

  // Authentication-based navigation items
  const authNavItems = currentUser 
    ? [
        { name: 'Dashboard', path: '/dashboard' }
      ]
    : [
        { name: 'Register', path: '/register' },
        { name: 'Login', path: '/login' }
      ];

  const allNavItems = [...baseNavItems, ...authNavItems];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2, color: 'primary.main', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>
        FOREX WARS
      </Typography>
      <List>
        {allNavItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <Button
              component={Link}
              to={item.path}
              fullWidth
              sx={{
                color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                justifyContent: 'center',
                py: 1.5,
              }}
            >
              <ListItemText primary={item.name} />
            </Button>
          </ListItem>
        ))}
        {currentUser && (
          <>
            <Divider sx={{ my: 1 }} />
            {isAdmin && (
              <ListItem disablePadding>
                <Button
                  component={Link}
                  to="/admin"
                  fullWidth
                  sx={{
                    color: location.pathname === '/admin' ? 'primary.main' : 'text.primary',
                    justifyContent: 'center',
                    py: 1.5,
                  }}
                >
                  <ListItemText primary="Admin Portal" />
                </Button>
              </ListItem>
            )}
            <ListItem disablePadding>
              <Button
                onClick={handleLogout}
                fullWidth
                sx={{
                  color: 'text.primary',
                  justifyContent: 'center',
                  py: 1.5,
                }}
              >
                <ListItemText primary="Logout" />
              </Button>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FXWarsLogo size={50} />
              <Typography
                variant="h4"
                component={Link}
                to="/"
                sx={{
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, #0052cc 0%, #2684ff 50%, #4c9aff 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textDecoration: 'none',
                  ml: 2,
                  letterSpacing: '1px',
                  fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' },
                  textTransform: 'uppercase',
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  textShadow: '0 2px 8px rgba(0,82,204,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.03)',
                    filter: 'brightness(1.2)',
                    textShadow: '0 4px 12px rgba(0,82,204,0.3)',
                  },
                }}
              >
                FOREX WARS
              </Typography>
            </Box>
          </motion.div>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ color: 'primary.main' }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {baseNavItems.map((item) => (
                <motion.div
                  key={item.name}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    component={Link}
                    to={item.path}
                    sx={{
                      color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                      fontWeight: location.pathname === item.path ? 600 : 400,
                      mx: 1,
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        width: location.pathname === item.path ? '100%' : '0%',
                        height: '2px',
                        bottom: 0,
                        left: '50%',
                        backgroundColor: 'primary.main',
                        transition: 'all 0.3s ease-in-out',
                        transform: 'translateX(-50%)',
                      },
                      '&:hover::after': {
                        width: '100%',
                      },
                    }}
                  >
                    {item.name}
                  </Button>
                </motion.div>
              ))}
              
              {currentUser ? (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                  <IconButton
                    onClick={handleMenuOpen}
                    sx={{ p: 0 }}
                  >
                    <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                      <AccountCircle />
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <Box sx={{ px: 2, py: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {userProfile?.firstName} {userProfile?.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {userProfile?.email}
                      </Typography>
                    </Box>
                    <Divider />
                    <MenuItem onClick={() => { navigate('/dashboard'); handleMenuClose(); }}>
                      <Dashboard sx={{ mr: 1 }} />
                      Dashboard
                    </MenuItem>
                    {isAdmin && (
                      <MenuItem onClick={() => { navigate('/admin'); handleMenuClose(); }}>
                        <AdminPanelSettings sx={{ mr: 1 }} />
                        Admin Portal
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>
                      <Logout sx={{ mr: 1 }} />
                      Logout
                    </MenuItem>
                  </Menu>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                  <Button
                    component={Link}
                    to="/login"
                    variant="outlined"
                    sx={{
                      borderColor: location.pathname === '/login' ? 'primary.main' : 'primary.main',
                      color: location.pathname === '/login' ? 'primary.main' : 'primary.main',
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    component={Link}
                    to="/register"
                    variant="contained"
                    sx={{
                      backgroundColor: location.pathname === '/register' ? 'primary.dark' : 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    }}
                  >
                    Register
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar; 